import React, { useEffect, useState, useMemo } from "react";
import { FaArrowLeft, FaList, FaTh } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import applicationService from "../services/applicationService";
import MyApplicationsTableView from "./MyApplicationsTableView";
import LoadingSpinner from "../../components/LoadingSpinner";
import { INFLUENCER_BASE } from "../constants";

const VIEW_MODE_BREAKPOINT = 768; // md: table on >= 768px, grid on < 768px
const getViewModeFromWidth = (width) => (width >= VIEW_MODE_BREAKPOINT ? "table" : "grid");

// Safely derive a readable company/brand name for an application
const getCompanyName = (application = {}) => {
  return application.companyId?.companyName || "Company not specified";
};

const MyApplications = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [rejectionInfo, setRejectionInfo] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Sync view mode with viewport: table on md+, grid on mobile
  useEffect(() => {
    const syncViewMode = () => setViewMode(getViewModeFromWidth(window.innerWidth));
    syncViewMode();
    window.addEventListener("resize", syncViewMode);
    return () => window.removeEventListener("resize", syncViewMode);
  }, []);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await applicationService.getMyApplications();
        setApplications(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load applications:", err);
        setError(err.message || "Failed to load applications");
        toast.error(err.message || "Failed to load applications");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const applicationIdFromUrl = searchParams.get("applicationId");

  useEffect(() => {
    if (loading || !applicationIdFromUrl || applications.length === 0) return;
    const app = applications.find((a) => a._id === applicationIdFromUrl);
    if (app) {
      setSelectedApplication(app);
      setSearchParams({}, { replace: true });
    }
  }, [loading, applicationIdFromUrl, applications, setSearchParams]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const normalizeProposedRate = (rate) => {
    if (rate === undefined || rate === null) return null;

    if (typeof rate === "number") {
      return { amount: rate, code: "INR", symbol: "₹" };
    }

    if (typeof rate === "object") {
      const amount = Number(rate.amount ?? rate.value ?? 0);
      const normalizedAmount = Number.isFinite(amount) ? Math.max(0, amount) : 0;
      const currency = rate.currency || {};
      return {
        amount: normalizedAmount,
        code: (currency.code || rate.code || "INR").toUpperCase(),
        symbol: currency.symbol || rate.symbol || "₹",
      };
    }

    const fallbackAmount = Number(rate);
    return Number.isFinite(fallbackAmount)
      ? { amount: Math.max(0, fallbackAmount), code: "INR", symbol: "₹" }
      : null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "bg-green-100 text-green-700";
      case "rejected":
        return "bg-red-100 text-red-700";
      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "accepted":
        return "Accepted";
      case "rejected":
        return "Rejected";
      default:
        return "Pending";
    }
  };

  const hasApplications = useMemo(() => applications.length > 0, [applications]);

  const filteredApplications = useMemo(() => {
    if (!Array.isArray(applications)) return [];
    if (statusFilter === "all") return applications;

    const normalizedFilter = statusFilter.toLowerCase();

    return applications.filter((app) => {
      const status = (app?.status || "pending").toLowerCase();
      return status === normalizedFilter;
    });
  }, [applications, statusFilter]);

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading your applications..." />
      </div>
    );
  }

  if (error && !hasApplications) {
    return (
      <div className="flex min-h-[60vh] w-full flex-col items-center justify-center p-4 lg:p-6">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-red-50 p-6 text-center shadow-sm">
          <p className="mb-4 text-sm font-semibold text-red-600 sm:text-base">{error}</p>
          <button
            type="button"
            onClick={() => navigate(`${INFLUENCER_BASE}/dashboard`)}
            className="rounded-xl bg-orange-500 px-5 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
      <button
        type="button"
        onClick={() => navigate(`${INFLUENCER_BASE}/dashboard`)}
        className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600 lg:mb-4"
      >
        <FaArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">My Applications</h1>
            <p className="mt-1 text-sm text-gray-500">
              Your campaign applications ({filteredApplications.length})
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {["all", "pending", "accepted", "rejected"].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(status)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition sm:px-4 sm:py-2 sm:text-sm ${
                  statusFilter === status
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {status === "all" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}

            <div className="ml-auto flex items-center gap-1 rounded-xl bg-gray-100 p-1 sm:ml-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition sm:gap-2 sm:px-4 sm:py-2 sm:text-sm ${
                  viewMode === "grid"
                    ? "bg-white text-orange-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FaTh />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition sm:gap-2 sm:px-4 sm:py-2 sm:text-sm ${
                  viewMode === "table"
                    ? "bg-white text-orange-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <FaList />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {!hasApplications ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
          <h3 className="text-base font-semibold text-gray-900">No applications yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Explore campaigns and submit your first application to see it here.
          </p>
        </div>
      ) : filteredApplications.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
          <h3 className="text-base font-semibold text-gray-900">No applications found</h3>
          <p className="mt-1 text-sm text-gray-500">Try a different status filter.</p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {filteredApplications.map((application) => {
            const proposedRate = normalizeProposedRate(application.proposedRate);
            return (
              <div
                key={application._id}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md"
              >
                <div className="relative border-b border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-bold text-gray-900">
                        {application.campaignId?.name || "Campaign"}
                      </h3>
                      <p className="truncate text-sm text-gray-600">{getCompanyName(application)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => application.status === "rejected" && setRejectionInfo(application)}
                      className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(
                        application.status
                      )} ${application.status === "rejected" ? "hover:opacity-80" : ""}`}
                      title={application.status === "rejected" ? "View rejection reason" : undefined}
                    >
                      {getStatusLabel(application.status)}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {application.campaignAdvertisingId?.hireType || "Requirement"}
                  </p>
                </div>

                <div className="p-4">
                  <div className="space-y-2 text-sm text-gray-700">
                    <div>
                      <p className="text-xs text-gray-500">Requirement</p>
                      <p className="line-clamp-2">
                        {application.campaignAdvertisingId?.requirement || "Not provided"}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-gray-50 p-2">
                        <p className="text-xs text-gray-500">Applied On</p>
                        <p className="font-semibold text-gray-900">{formatDate(application.createdAt)}</p>
                      </div>
                      <div className="rounded-xl bg-gray-50 p-2">
                        <p className="text-xs text-gray-500">Proposed Rate</p>
                        <p className="font-semibold text-gray-900">
                          {proposedRate
                            ? `${proposedRate.symbol || ""} ${proposedRate.amount} ${proposedRate.code || ""}`
                            : "Not shared"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedApplication(application)}
                    className="mt-4 w-full rounded-xl px-4 py-2 text-sm font-medium text-orange-600 transition hover:bg-orange-50 hover:text-orange-700"
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <MyApplicationsTableView
          applications={filteredApplications}
          formatDate={formatDate}
          getStatusColor={getStatusColor}
          onViewDetails={setSelectedApplication}
          onViewRejection={setRejectionInfo}
          normalizeProposedRate={normalizeProposedRate}
          getCompanyName={getCompanyName}
        />
      )}

      {selectedApplication && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center overflow-y-auto bg-black/50 p-3 sm:p-4"
          onClick={() => setSelectedApplication(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="application-detail-title"
        >
          <div
            className="my-auto flex max-h-[70vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
              <h2 id="application-detail-title" className="truncate pr-2 text-lg font-bold text-gray-900 sm:text-xl">
                {selectedApplication.campaignId?.name || "Campaign"} – Details
              </h2>
              <button
                type="button"
                onClick={() => setSelectedApplication(null)}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-2xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="text-xs font-medium uppercase text-gray-500">Status</span>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ${getStatusColor(
                    selectedApplication.status
                  )}`}
                >
                  {getStatusLabel(selectedApplication.status)}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Campaign</p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {selectedApplication.campaignId?.name || "Campaign"}
                  </p>
                  <p className="mt-1 text-sm text-gray-600">{getCompanyName(selectedApplication)}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {selectedApplication.campaignAdvertisingId?.hireType || "Requirement"}
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Applied On</p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {formatDate(selectedApplication.createdAt)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Proposed Rate</p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {(() => {
                      const rate = normalizeProposedRate(selectedApplication.proposedRate);
                      return rate
                        ? `${rate.symbol || ""} ${rate.amount} ${rate.code || ""}`.trim()
                        : "Not shared";
                    })()}
                  </p>
                </div>
              </div>

              {selectedApplication.campaignAdvertisingId?.requirement && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                  <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">Requirement</p>
                  <p className="text-sm text-gray-800">
                    {selectedApplication.campaignAdvertisingId.requirement}
                  </p>
                </div>
              )}

              {selectedApplication.insight && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                  <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">Insight you shared</p>
                  <p className="whitespace-pre-line text-sm text-gray-800">{selectedApplication.insight}</p>
                </div>
              )}

              {selectedApplication.rejectionReason && selectedApplication.status === "rejected" && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 sm:p-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-red-700">
                    Rejection Details
                  </p>
                  <p className="text-sm text-red-800">
                    <span className="font-semibold">Reason: </span>
                    {selectedApplication.rejectionReason}
                  </p>
                  {selectedApplication.rejectionNotes && (
                    <p className="mt-1 text-sm text-red-800">
                      <span className="font-semibold">Notes: </span>
                      {selectedApplication.rejectionNotes}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {rejectionInfo && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setRejectionInfo(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-gray-900">Rejection Reason</h3>
              <button
                type="button"
                onClick={() => setRejectionInfo(null)}
                className="rounded-xl p-1 text-2xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
              >
                &times;
              </button>
            </div>
            <div className="space-y-3 p-6">
              <div>
                <p className="mb-1 text-xs text-gray-500">Reason</p>
                <p className="text-sm font-semibold text-gray-900">
                  {rejectionInfo.rejectionReason || "Not provided"}
                </p>
              </div>
              {rejectionInfo.rejectionNotes && (
                <div>
                  <p className="mb-1 text-xs text-gray-500">Notes</p>
                  <p className="whitespace-pre-line text-sm text-gray-800">{rejectionInfo.rejectionNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApplications;
